import { getSessionStorage, setSessionStorage } from './sessionStorage';
import { checkCircularConfig, recordCircularConfig } from './configHook/failure';

export type Secret = {
    apiURL: string;
    statusPageURL: string;
    lastCheck?: string;
};

const updateSecrets = async (): Promise<boolean> => {
    const response = await fetch('assets/configs/secrets.json');

    if (response.ok) {
        const secrets: Secret = await response.json();

        // Check for circular configuration - apiURL pointing to itself
        if (checkCircularConfig(secrets)) {
            recordCircularConfig('secrets', secrets);
            // eslint-disable-next-line no-console
            console.error('Setup error: Server address points to this website');
            return false;
        }

        // Check if we have a cached version and if it's older than 1 day
        const cachedSecrets = getSessionStorage('secrets') as Secret | null;

        if (cachedSecrets?.lastCheck) {
            const lastCheck = new Date(cachedSecrets.lastCheck);
            const oneDayAgo = new Date();
            oneDayAgo.setDate(oneDayAgo.getDate() - 1);

            if (lastCheck > oneDayAgo) {
                // Secrets are fresh, no need to update localStorage
                return true;
            }
        }

        secrets.lastCheck = new Date().toISOString();

        setSessionStorage('secrets', secrets);
        return true;
    } else {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch secrets:', response.statusText);
        return false;
    }
};

/**
 * Get the secrets from local storage or fetch them from the server.
 * @returns {Promise<Secret>} The secrets object
 * @description This function retrieves the secrets from local storage. If the secrets are not found, it fetches them from the server and stores them in local storage.
 */
const getSecrets = async (): Promise<Secret> => {
    const secrets = getSessionStorage('secrets');

    if (!secrets) {
        await updateSecrets();
        return getSecrets();

    }

    return secrets as Secret;

};

export {
    getSecrets,
    updateSecrets
};
